'use client';

// 배럴(Assets/Icon)이 아니라 파일을 직접 import 한다 — 테스트가 배럴을 일부 심볼만 가진 객체로
// mock 하는 경우 새 아이콘을 배럴로 읽으면 렌더 시 throw 한다 (RenewalNoticeModal 과 같은 이유).
import SvgNoticeMegaphone from 'Assets/Icon/svgs/NoticeMegaphone.svg';
import SvgRenewalClose from 'Assets/Icon/svgs/RenewalClose.svg';
import { Button, Column, Modal, Typography } from '@viewup/ui';
import Image from 'next/image';

export interface PointNotationNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClickNotice: () => void;
  /** '다시 보지 않기' 버튼. onClose 와 달리 다시 열지 않게 하는 쪽이다. */
  onNeverShowAgain: () => void;
}

// 피그마 신규 디자인(포인트 표기방식 변경 안내 팝업)의 색. 대응하는 theme 토큰이 없다 —
// RenewalNoticeModal 의 DESIGN_COLOR 와 같은 값에 본문 검정이 더해졌다.
const DESIGN_COLOR = {
  brand: '#0077FF',
  textBody: '#000000',
  textPrimary: '#001E40',
  textSecondary: '#576A7C',
};

// sds Button(size XL)은 고정 height·좌우 padding·body.regular 라벨이라 디자인(높이 48 = 세로 padding 12 +
// 라벨 Bold 16/24, 폭 100%)과 맞지 않아 보충한다. 라벨은 Button 내부 Typography(span)를 겨냥한다.
// hover/active 는 variant 가 :hover/:active 로 색을 덮어쓰므로 같은 셀렉터로 다시 눌러준다.
const NOTICE_BUTTON_CSS = {
  '& span': { fontSize: 16, fontWeight: 700, lineHeight: '24px' },
  ':active': { backgroundColor: DESIGN_COLOR.brand },
  ':hover': { backgroundColor: DESIGN_COLOR.brand },
  backgroundColor: DESIGN_COLOR.brand,
  borderRadius: 8,
  boxSizing: 'border-box',
  color: '#FFFFFF',
  height: 48,
  minHeight: 0,
  paddingBlock: 0,
  width: '100%',
} as const;

const CLOSE_BUTTON_STYLE = {
  background: 'none',
  border: 0,
  cursor: 'pointer',
  display: 'block',
  height: 32,
  padding: 0,
  position: 'absolute',
  right: 20,
  top: 20,
  width: 32,
} as const;

const NEVER_SHOW_AGAIN_BUTTON_STYLE = {
  background: 'none',
  border: 0,
  color: DESIGN_COLOR.textSecondary,
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: '22px',
  // 터치 영역을 높이 38 로 키운다(라벨 22 + 위아래 8). 시각 간격은 위 20(marginTop 12 + padding 8)이 유지된다.
  marginTop: 12,
  padding: '8px 12px',
  textAlign: 'center',
  textDecoration: 'none',
} as const;

export const PointNotationNoticeModal = ({
  isOpen,
  onClose,
  onClickNotice,
  onNeverShowAgain,
}: PointNotationNoticeModalProps) => {
  return (
    // sds Modal 은 radix dialog 를 포털로 띄우고 dim(bg.dimmer.60 = rgba(0,0,0,0.6))·중앙 정렬·
    // 흰 카드를 이미 제공한다. 카드 폭만 좁은 폰에서도 좌우 20px 여백이 남도록 보충한다.
    <Modal
      exitHandler={onClose}
      hasCloseButton={false}
      height='auto'
      isOpen={isOpen}
      isResponsive={false}
      styledComponents={{
        root: {
          css: { borderRadius: 8, maxWidth: 360, whiteSpace: 'normal' },
          p: 0,
        },
      }}
      width='calc(100% - 40px)'
    >
      <button aria-label='닫기' onClick={onClose} style={CLOSE_BUTTON_STYLE} type='button'>
        <Image alt='' height={32} src={SvgRenewalClose} width={32} />
      </button>

      {/* 카드 높이 354 = 위 32 + 아이콘 56 + 8 + 제목 36 + 8 + 본문 72 + 20 + 버튼 48 + 20 + 다시 보지 않기 22 + 아래 32 */}
      <Column alignItems='center' css={{ boxSizing: 'border-box', padding: '32px 20px 24px' }}>
        <Image alt='' height={56} src={SvgNoticeMegaphone} width={56} />

        <Typography
          css={{
            color: DESIGN_COLOR.textPrimary,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: '36px',
            marginTop: 8,
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
          fontType='header2.bold'
        >
          <span style={{ color: DESIGN_COLOR.brand }}>포인트 표기</span>가 변경됐어요!
        </Typography>

        <Typography
          css={{
            color: DESIGN_COLOR.textBody,
            lineHeight: '24px',
            marginBottom: 20,
            marginTop: 8,
            textAlign: 'center',
            width: 267,
          }}
          fontType='body.regular'
        >
          적립 완료 포인트와 적립 예정 포인트를
          <br />
          구분해 확인할 수 있어요!
          <br />
          달라진 포인트 내역을 확인해 보세요!
        </Typography>

        <Button css={NOTICE_BUTTON_CSS} onClick={onClickNotice} size='XL' variant='solid.primary'>
          공지사항 보러가기
        </Button>

        <button onClick={onNeverShowAgain} style={NEVER_SHOW_AGAIN_BUTTON_STYLE} type='button'>
          다시 보지 않기
        </button>
      </Column>
    </Modal>
  );
};
